/*
 * THEIR SEEDED RANDOM — mulberry32, ported bit for bit.
 *
 * From `reference/universe-v8/engine.js` line 240.
 *
 * WHY A PRNG IS PART OF THE PORT AT ALL. Five layers of their renderer are generated rather than
 * authored: the dust beyond the rim, the star field, the passage text, the reader avatars, the layout
 * jitter. All of them call this with a fixed seed, which is what makes them stable — the dust does
 * not shimmer between frames and the stars stay where they were. It also makes them CHECKABLE: a
 * seeded generator is a pure function, so a layer built from one either matches theirs exactly or
 * does not, and there is nothing to argue about.
 *
 * `Math.random()` would have been the obvious thing and would have cost both properties.
 *
 * Faithful to the arithmetic, including every `|0` and `>>>`: they are not tidiness, they are the
 * algorithm. `Math.imul` is a 32-bit multiply, and `>>> 0` reads the result as unsigned. Change any
 * of it and the sequence is a different sequence.
 */

/** A stream of numbers in `[0, 1)`, the same stream every time for the same seed. */
export type Random = () => number;

/** 2³², the divisor that turns a 32-bit word into a fraction. */
const WORD = 4294967296;

/**
 * A generator seeded by one integer.
 *
 * Their seeds are arbitrary constants (9091 for the dust, 4711 for one star field, 20260814 for the
 * layout) and are MECHANISM rather than data: which numbers come out matters, what they mean does
 * not, and a caller that wants a different field asks for a different seed.
 */
export function mulberry32(seed: number): Random {
  let a = seed;
  return (): number => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / WORD;
  };
}
